import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';


// Lazy load tab components for better initial load performance
const AnalyticsOverview = lazy(() => import('./AnalyticsOverview'));
const AnalyticsTokens = lazy(() => import('./AnalyticsTokens'));
const AnalyticsSessions = lazy(() => import('./AnalyticsSessions'));
const AnalyticsConversations = lazy(() => import('./AnalyticsConversations'));
const AnalyticsCost = lazy(() => import('./AnalyticsCost'));
const AnalyticsSentiment = lazy(() => import('./AnalyticsSentiment'));

const Analytics = () => {
  const [activeAiAgentId, setActiveAiAgentId] = useState(null);
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    const aiAgentId = localStorage.getItem('activeAiAgentId');
    if (aiAgentId) {
      setActiveAiAgentId(aiAgentId);
    }
  }, []);

  if (!activeAiAgentId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
        <div className="premium-spinner mb-4" />
        <div className="text-slate-400 font-bold text-xs uppercase tracking-wider font-semibold">Identifying Active Agent...</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
:root {
  --grab-blue: #83bbe1;
  --grab-blue-light: #f0f7ff;
  --grab-blue-dark: #2563eb;
  --slate-50: #f8fafc;
  --slate-100: #f1f5f9;
  --slate-200: #e2e8f0;
  --slate-400: #94a3b8;
  --slate-500: #64748b;
  --slate-600: #475569;
  --slate-800: #1e293b;
  --slate-900: #0f172a;
}

.premium-spinner {
  border: 3px solid var(--slate-100);
  border-top: 3px solid var(--grab-blue);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: premium-spin 0.8s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes premium-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.glass-card {
  background: #ffffff;
  border: 1px solid var(--slate-200);
  box-shadow: 0 1px 3px rgba(0,0,0,0.02), 0 1px 2px rgba(0,0,0,0.04);
  border-radius: 20px;
}

.glass-card:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  transform: translateY(-2px);
}

.glass-card:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -6px rgba(0, 0, 0, 0.02);
  border-color: #e5e7eb;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f8fafc;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 20px;
  border: 2px solid #f8fafc;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

.custom-scrollbar:hover::-webkit-scrollbar-thumb {
  background: #94a3b8;
}

/* Premium Spinner */
.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1.5rem;
}

.premium-spinner {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: 4px solid #f3f4f6; /* Gray-100 */
  border-top-color: var(--grab-blue);
  animation: premium-spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  box-shadow: 0 0 20px rgba(131, 187, 225, 0.1);
}

@keyframes premium-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 0.75rem;
  font-weight: 800;
  color: #4b5563; /* Gray-600 */
  letter-spacing: 0.15em;
  text-transform: uppercase;
  animation: text-pulse 2s ease-in-out infinite;
}

@keyframes text-pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* Chart Specific Loading */
.chart-container {
  position: relative;
  width: 100%;
  height: 300px;
}

.chart-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
  animation: fadeIn 0.3s ease-out;
}

.chart-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f4f6;
  border-top-color: var(--grab-blue);
  border-radius: 50%;
  animation: premium-spin 0.8s linear infinite;
}

/* Premium Trace Explorer Styles (Agnodash Inspired) */

.traces-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border: 1px solid transparent;
}

.traces-chip.llm {
    background: #eff6ff;
    color: #2563eb;
    border-color: #dbeafe;
}

.traces-chip.tool {
    background: #fff7ed;
    color: #ea580c;
    border-color: #ffedd5;
}

.traces-chip.agent {
    background: #f0f7ff;
    color: #2563eb;
    border-color: #e0f2fe;
}

.traces-chip.step {
    background: #f8fafc;
    color: #64748b;
    border-color: #f1f5f9;
}

.traces-view-toggle {
    display: flex;
    background: #f1f5f9;
    padding: 2px;
    border-radius: 8px;
    width: fit-content;
    border: 1px solid #e2e8f0;
}

.traces-view-toggle button {
    padding: 4px 12px;
    font-size: 10px;
    font-weight: 800;
    border-radius: 6px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    color: #64748b;
}

.traces-view-toggle button.active {
    background: #fff;
    color: #0f172a;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.convo-subtree-wrapper {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
}

.convo-subtree-wrapper.is-expanded {
    grid-template-rows: 1fr;
}

.convo-subtree-inner {
    min-height: 0;
    padding: 12px 0 12px 24px;
    margin-left: 12px;
    animation: fadeIn 0.4s ease-out;
}

.convo-trace-tree {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.animate-slideDown {
    animation: slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
}

.trace-type-icon-img {
    width: 14px;
    height: 14px;
    object-fit: contain;
}

/* Fix for nested trace items background */
.convo-trace-item {
    background: white;
    transition: transform 0.2s;
}

.convo-trace-item:hover {
    transform: translateX(4px);
}

.analytics-page table th {
    font-weight: 600;
    letter-spacing: 0.025em;
    text-transform: uppercase;
    font-size: 0.75rem;
}
      `}</style>
      <div className="min-h-screen bg-white font-['Inter',sans-serif] text-zinc-900 pb-20 w-full min-w-0 flex-1 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-8 space-y-6">
        {/* Tab switcher removed as requested */}
        
        <div className="w-full">
          <Suspense fallback={
            <div className="space-y-4 animate-pulse">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 h-24 flex flex-col justify-between">
                    <div className="h-2.5 w-20 bg-zinc-200 rounded" />
                    <div className="h-6 w-16 bg-zinc-200 rounded" />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="h-44 rounded-xl border border-zinc-200 bg-zinc-50/40" />
                <div className="h-44 rounded-xl border border-zinc-200 bg-zinc-50/40" />
              </div>
            </div>
          }>
            {currentTab === 'overview' && <AnalyticsOverview activeAiAgentId={activeAiAgentId} />}
            {currentTab === 'token-analytics' && <AnalyticsTokens activeAiAgentId={activeAiAgentId} />}
            {currentTab === 'cost-analytics' && <AnalyticsCost activeAiAgentId={activeAiAgentId} />}
            {currentTab === 'sessions' && <AnalyticsSessions activeAiAgentId={activeAiAgentId} />}
            {currentTab === 'conversations' && <AnalyticsConversations activeAiAgentId={activeAiAgentId} />}
            {currentTab === 'sentiment' && <AnalyticsSentiment activeAiAgentId={activeAiAgentId} />}
          </Suspense>
        </div>
      </div>
    </div>
    </>
  );
};

export default Analytics;
